// /home/ladmin/ESTATA/apps/0001-estata.com/frontend/src/components/AnchorMenu.tsx

import { useEffect, useState, useRef } from "react";
import Container from "../hooks/Container.tsx";
import { ChevronDown } from "lucide-react";

interface AnchorMenuItem {
  id: string;
  label: string;
  anchor: string;
}

interface AnchorMenuProps {
  items: AnchorMenuItem[];
  className?: string;
  title?: string;
  titleLink?: string;
  height?: number;
}

// ==========================================
// ZENTRALE KONSTANTEN - MUSS MIT HEADERDESKTOP ÜBEREINSTIMMEN!
// ==========================================
const HEADER_HEIGHT = 50; // px - Muss identisch mit HeaderDesktop sein!
const SCROLL_THRESHOLD = 10; // px
const SCROLL_DELTA_MIN = 5; // px

const AnchorMenu: React.FC<AnchorMenuProps> = ({ 
  items, 
  className = "", 
  title = "Services",
  titleLink = "#top",
  height = 50
}) => {
  const [activeAnchor, setActiveAnchor] = useState<string>("");
  const [isSticky, setIsSticky] = useState(false);
  const [scrollingTo, setScrollingTo] = useState<string | null>(null);
  const [isScrolling, setIsScrolling] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // NEU: Header-Visibility-State (synchron mit HeaderDesktop)
  const [headerVisible, setHeaderVisible] = useState(true);
  const lastScrollY = useRef(0);
  const scrollDirection = useRef<'up' | 'down' | null>(null);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  // Scroll-basierte Aktivierung des aktiven Ankers + Header-Visibility
  useEffect(() => {
    let scrollTimeout: number;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const delta = currentScrollY - lastScrollY.current;

      // Header-Visibility-Logic (identisch mit HeaderDesktop)
      if (Math.abs(delta) >= SCROLL_DELTA_MIN) {
        const isScrollingDown = delta > 0;
        const isScrollingUp = delta < 0;

        if (isScrollingDown && currentScrollY > SCROLL_THRESHOLD) {
          if (scrollDirection.current !== 'down') {
            scrollDirection.current = 'down';
            setHeaderVisible(false);
          }
        }

        if (isScrollingUp) {
          if (scrollDirection.current !== 'up') {
            scrollDirection.current = 'up';
            setHeaderVisible(true);
          }
        }

        if (currentScrollY <= SCROLL_THRESHOLD) {
          setHeaderVisible(true);
          scrollDirection.current = null;
        }

        lastScrollY.current = currentScrollY;
      }

      // Sticky-State
      setIsSticky(window.scrollY > 50);
      setIsScrolling(true);
      clearTimeout(scrollTimeout);
      
      scrollTimeout = window.setTimeout(() => {
        setIsScrolling(false);
        setScrollingTo(null);
      }, 150);

      const scrollPosition = window.scrollY + 150;
      
      // Top-Bereich
      if (scrollPosition < 200) {
        setActiveAnchor("top");
        return;
      }
      
      // Sections durchlaufen
      for (let i = 0; i < items.length; i++) {
        const section = document.getElementById(items[i].anchor);
        if (section) {
          const sectionTop = section.offsetTop;
          const sectionBottom = sectionTop + section.offsetHeight;
          
          if (scrollPosition >= sectionTop && scrollPosition < sectionBottom) {
            setActiveAnchor(items[i].anchor);
            return;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [items]);

  // Smooth Scroll zu Anker
  const handleAnchorClick = (e: React.MouseEvent<HTMLAnchorElement>, anchor: string) => {
    e.preventDefault();
    setScrollingTo(anchor);
    setIsMobileMenuOpen(false);
    
    if (anchor === "top" || anchor === "#top") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      const element = document.getElementById(anchor);
      if (element) {
        const offset = 120;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        window.scrollTo({ top: offsetPosition, behavior: "smooth" });
      }
    }
  };

  const isLinkActive = (anchor: string) => {
    return activeAnchor === anchor || scrollingTo === anchor;
  };

  const getActiveLabel = () => {
    const activeItem = items.find(item => isLinkActive(item.anchor));
    return activeItem ? activeItem.label : items[0]?.label || "Menü";
  };

  // Dynamische Transform: Synchron mit Header!
  const getTransform = () => {
    return headerVisible ? 'translateY(0)' : `translateY(-${HEADER_HEIGHT}px)`;
  };

  return (
    <nav
      className={`
        z-20
        sticky
        ${isSticky ? 'shadow-lg' : ''}
        ${className}
      `}
      style={{
        background: 'linear-gradient(90deg, #7C4FBD 100%)',
        height: `${height}px`,
        top: `${HEADER_HEIGHT}px`, // Statisch: Immer unter Header-Höhe
        transform: getTransform(), // Dynamisch: Gleitet mit Header!
        transition: 'transform 300ms ease-in-out' // Gleiche Transition wie Header
      }}
    >
      <Container>
        <div 
          className="flex items-center justify-between"
          style={{ height: `${height}px` }}
        >
          {/* Title als Link - Links (immer sichtbar) */}
          <div className="flex-shrink-0 h-full flex items-center">
            <a
              href={titleLink}
              onClick={(e) => handleAnchorClick(e, "top")}
              className={`
                text-white font-normal text-sm
                transition-all duration-200
                whitespace-nowrap
                h-full flex items-center px-4
                hover:bg-white/15
                ${isLinkActive("top") ? 'bg-white/20' : ''}
              `}
            >
              {title}
            </a>
          </div>

          {/* Desktop: Anchor Links - Rechts */}
          <ul className="hidden md:flex justify-end h-full items-center">
            {items.map((item) => (
              <li key={item.id} className="h-full flex items-center">
                <a
                  href={`#${item.anchor}`}
                  onClick={(e) => handleAnchorClick(e, item.anchor)}
                  className={`
                    text-white font-normal text-sm
                    transition-all duration-200
                    whitespace-nowrap
                    h-full flex items-center px-4
                    hover:bg-white/20
                    ${isLinkActive(item.anchor) ? 'bg-white/20' : ''}
                  `}
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ul>

          {/* Mobile: Dropdown - Rechts */}
          <div ref={dropdownRef} className="md:hidden flex-shrink-0 h-full flex items-center relative">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`
                text-white font-normal text-sm h-full flex items-center px-4 transition-all duration-200
                ${activeAnchor && activeAnchor !== 'top' ? 'bg-white/20 hover:bg-white/25' : 'bg-[#7C4FBD]'}
              `}
            >
              <span>{getActiveLabel()}</span>
              <ChevronDown 
                className={`w-4 h-4 ml-2 transition-transform duration-200 ${
                  isMobileMenuOpen ? 'rotate-180' : ''
                }`}
              />
            </button>

            {/* Dropdown Menu */}
            {isMobileMenuOpen && (
              <div 
                className="absolute top-full right-0 bg-[#7C4FBD] border-t border-white/20 shadow-lg min-w-[200px]"
                style={{ marginTop: '0px' }}
              >
                {items.map((item, index) => (
                  <a
                    key={item.id}
                    href={`#${item.anchor}`}
                    onClick={(e) => handleAnchorClick(e, item.anchor)}
                    className={`
                      block px-4 py-3 text-sm text-white hover:bg-white/20 transition-colors
                      ${index > 0 ? 'border-t border-white/20' : ''}
                      ${isLinkActive(item.anchor) ? 'bg-white/20' : ''}
                    `}
                  >
                    {item.label}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </Container>
    </nav>
  );
};

export default AnchorMenu;